import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Sparkles } from 'lucide-react'
import { api, apiErrorMessage } from '../lib/api'
import type { KeysOut, MeOut, StatusOut } from '../lib/api'
import { MultiCsvUpload } from '../components/MultiCsvUpload'

export function Settings() {
  return (
    <div className="px-8 py-6 max-w-3xl mx-auto space-y-8">
      <header>
        <h1 className="text-2xl font-semibold text-gray-100">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Personal context, API keys, and connections.
        </p>
      </header>

      <ICPSection />
      <ConnectionsSection />
      <KeysSection />
    </div>
  )
}

function ConnectionsSection() {
  const qc = useQueryClient()
  const statusQ = useQuery<StatusOut>({
    queryKey: ['status'],
    queryFn: async () => (await api.get<StatusOut>('/status')).data,
  })

  interface JobOut {
    id: string
    status: 'pending' | 'running' | 'done' | 'failed'
    progress_current: number
    progress_total: number
    error?: string | null
  }
  const [enrichJobId, setEnrichJobId] = useState<string | null>(null)

  const startEnrich = useMutation({
    mutationFn: async () => (await api.post<JobOut>('/enrich/profile')).data,
    onSuccess: (data) => setEnrichJobId(data.id),
  })

  const enrichJobQ = useQuery<JobOut>({
    queryKey: ['job', enrichJobId],
    queryFn: async () => (await api.get<JobOut>(`/jobs/${enrichJobId}`)).data,
    enabled: !!enrichJobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'done' || s === 'failed' ? false : 5000
    },
  })

  const total = statusQ.data?.total_connections ?? 0
  const enriched = statusQ.data?.enriched ?? 0
  const pending = Math.max(0, total - enriched)
  const enrichRunning = enrichJobQ.data?.status === 'running' || enrichJobQ.data?.status === 'pending'

  return (
    <section className="card p-6 space-y-5">
      <div>
        <h2 className="text-lg font-medium text-gray-100">Connections</h2>
        <p className="text-sm text-gray-500">
          Add more LinkedIn exports to grow your network. Duplicates are skipped automatically.
        </p>
        <div className="text-sm text-gray-400 mt-2">
          {total.toLocaleString()} total · {enriched.toLocaleString()} enriched · {pending.toLocaleString()} pending
        </div>
      </div>

      <MultiCsvUpload
        onComplete={() => qc.invalidateQueries({ queryKey: ['status'] })}
      />

      {pending > 0 && (
        <div className="border-t border-border/60 pt-5">
          <div className="text-sm text-gray-300">
            {pending.toLocaleString()} new {pending === 1 ? 'profile is' : 'profiles are'} pending enrichment.
            Estimated cost: ~${(pending * 0.005).toFixed(2)} on Apify.
          </div>
          <div className="flex items-center justify-end mt-3">
            <button
              className="btn-secondary"
              disabled={startEnrich.isPending || enrichRunning}
              onClick={() => startEnrich.mutate()}
            >
              {startEnrich.isPending || enrichRunning ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Enriching…
                </>
              ) : (
                <>
                  <Sparkles size={14} /> Enrich {pending.toLocaleString()} pending
                </>
              )}
            </button>
          </div>
          {enrichJobQ.data && enrichRunning && (
            <div className="text-xs text-gray-500 mt-2">
              {enrichJobQ.data.progress_current} / {enrichJobQ.data.progress_total} profiles fetched
            </div>
          )}
          {enrichJobQ.data?.status === 'failed' && (
            <div className="text-sm text-red-400 mt-2">{enrichJobQ.data.error || 'Enrichment failed'}</div>
          )}
          {startEnrich.isError && (
            <div className="text-sm text-red-400 mt-2">{apiErrorMessage(startEnrich.error)}</div>
          )}
        </div>
      )}
    </section>
  )
}

function ICPSection() {
  const qc = useQueryClient()
  const meQ = useQuery<MeOut>({
    queryKey: ['me'],
    queryFn: async () => (await api.get<MeOut>('/me')).data,
  })
  const [prefs, setPrefs] = useState('')
  const [linkedinUrl, setLinkedinUrl] = useState('')
  const [synced, setSynced] = useState(false)

  if (meQ.data && !synced) {
    setPrefs(meQ.data.preferences ?? '')
    setLinkedinUrl(meQ.data.profile?.linkedin_url ?? '')
    setSynced(true)
  }

  const savePrefs = useMutation({
    mutationFn: async () => {
      const res = await api.put<MeOut>('/me/preferences', { preferences: prefs })
      return res.data
    },
    onSuccess: (data) => qc.setQueryData(['me'], data),
  })

  const enrichMe = useMutation({
    mutationFn: async () => {
      const res = await api.post<MeOut>('/me/enrich', { linkedin_url: linkedinUrl })
      return res.data
    },
    onSuccess: (data) => qc.setQueryData(['me'], data),
  })

  return (
    <section className="card p-6 space-y-5">
      <div>
        <h2 className="text-lg font-medium text-gray-100">About me (ICP)</h2>
        <p className="text-sm text-gray-500">
          Adds context about who's asking. Influences how reasoning judges fit.
        </p>
      </div>

      <div className="space-y-1.5">
        <label className="label">What are you trying to do?</label>
        <textarea
          className="input min-h-[100px] resize-y"
          placeholder="e.g. Building HR-tech for mid-market. Looking for design partners and seed investors."
          value={prefs}
          onChange={(e) => setPrefs(e.target.value)}
        />
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-gray-500">
            Stored locally with your account.
          </span>
          <button
            className="btn-primary"
            disabled={savePrefs.isPending || !synced}
            onClick={() => savePrefs.mutate()}
          >
            {savePrefs.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
          </button>
        </div>
        {savePrefs.isError && (
          <div className="text-sm text-red-400 mt-2">{apiErrorMessage(savePrefs.error)}</div>
        )}
      </div>

      <div className="border-t border-border/60 pt-5 space-y-1.5">
        <label className="label">Your LinkedIn URL (optional, ~$0.005 to enrich)</label>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="https://www.linkedin.com/in/your-handle"
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
          />
          <button
            className="btn-secondary"
            disabled={!linkedinUrl.trim() || enrichMe.isPending}
            onClick={() => enrichMe.mutate()}
          >
            {enrichMe.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Enriching…
              </>
            ) : (
              'Enrich me'
            )}
          </button>
        </div>
        {meQ.data?.has_profile && meQ.data.profile && (
          <div className="text-sm text-gray-300 pt-2">
            <span className="text-gray-500">Saved profile:</span>{' '}
            <span className="font-medium">{meQ.data.profile.headline || meQ.data.profile.current_title || '—'}</span>
            {meQ.data.profile.current_company && ` at ${meQ.data.profile.current_company}`}
          </div>
        )}
        {enrichMe.isError && (
          <div className="text-sm text-red-400 mt-2">{apiErrorMessage(enrichMe.error)}</div>
        )}
      </div>
    </section>
  )
}

function KeysSection() {
  const qc = useQueryClient()
  const keysQ = useQuery<KeysOut>({
    queryKey: ['keys'],
    queryFn: async () => (await api.get<KeysOut>('/keys')).data,
  })

  const [apify, setApify] = useState('')
  const [openai, setOpenai] = useState('')
  const [cohere, setCohere] = useState('')
  const [anthropic, setAnthropic] = useState('')

  const updateKeys = useMutation({
    mutationFn: async () => {
      const body: Record<string, string | undefined> = {}
      if (apify) body.apify_token = apify
      if (openai) body.openai_api_key = openai
      if (cohere) body.cohere_api_key = cohere
      if (anthropic) body.anthropic_api_key = anthropic
      const res = await api.put<KeysOut>('/keys', body)
      return res.data
    },
    onSuccess: (data) => {
      qc.setQueryData(['keys'], data)
      setApify(''); setOpenai(''); setCohere(''); setAnthropic('')
    },
  })

  const fields: { id: keyof KeysOut & `${string}_set`; last4Key: keyof KeysOut; label: string; value: string; setter: (v: string) => void; placeholder: string }[] = [
    { id: 'apify_token_set', last4Key: 'apify_last4', label: 'Apify token', value: apify, setter: setApify, placeholder: 'apify_api_…' },
    { id: 'openai_set', last4Key: 'openai_last4', label: 'OpenAI API key', value: openai, setter: setOpenai, placeholder: 'sk-…' },
    { id: 'cohere_set', last4Key: 'cohere_last4', label: 'Cohere API key', value: cohere, setter: setCohere, placeholder: '' },
    { id: 'anthropic_set', last4Key: 'anthropic_last4', label: 'Anthropic API key', value: anthropic, setter: setAnthropic, placeholder: 'sk-ant-…' },
  ]

  return (
    <section className="card p-6 space-y-5">
      <div>
        <h2 className="text-lg font-medium text-gray-100">API keys</h2>
        <p className="text-sm text-gray-500">
          Stored encrypted at rest. Required to run the pipeline.
        </p>
      </div>

      <div className="space-y-3">
        {fields.map((f) => (
          <div key={f.id} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="label">{f.label}</label>
              <span className="text-xs text-gray-500">
                {keysQ.data?.[f.id]
                  ? <span className="text-green-400">Set · {keysQ.data[f.last4Key]}</span>
                  : <span className="text-gray-500">Not set</span>}
              </span>
            </div>
            <input
              type="password"
              className="input"
              placeholder={
                keysQ.data?.[f.id]
                  ? 'Paste new value to replace, or leave blank to keep'
                  : f.placeholder
              }
              value={f.value}
              onChange={(e) => f.setter(e.target.value)}
              autoComplete="off"
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-end pt-2">
        <button
          className="btn-primary"
          disabled={updateKeys.isPending || !(apify || openai || cohere || anthropic)}
          onClick={() => updateKeys.mutate()}
        >
          {updateKeys.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Save keys'}
        </button>
      </div>
      {updateKeys.isError && (
        <div className="text-sm text-red-400">{apiErrorMessage(updateKeys.error)}</div>
      )}
    </section>
  )
}
